/**
 * One-shot recipes for the things the world does: feet, jumps, doors, the
 * checkpoint chime, breaking glass, the power going out, and every object the
 * run picks up or puts down.
 *
 * Each is `(v, t, g)` - where to schedule, when, and at what gain - and each is
 * side-effect only: it hangs nodes on the AudioContext timeline and returns.
 * The people are next door in `voices.ts`, and the ingredients they are all
 * built from are in `synth.ts`.
 */

import { noiseBurst, tone, type Voicing } from './synth';

export function footstep(v: Voicing, t: number, g: number): void {
  // Every step is a slightly different shoe on a slightly different board.
  const pitch = 0.85 + v.rng() * 0.3;
  noiseBurst(v, {
    at: t,
    duration: 0.11,
    peak: 0.5 * g,
    attack: 0.003,
    filter: 'lowpass',
    freq: 1900 * pitch,
    freqEnd: 420,
    q: 0.7,
  });
  tone(v, {
    type: 'sine',
    freq: 92 * pitch,
    freqEnd: 58,
    at: t,
    duration: 0.09,
    peak: 0.35 * g,
    attack: 0.003,
  });
}

export function jump(v: Voicing, t: number, g: number): void {
  noiseBurst(v, {
    at: t,
    duration: 0.09,
    peak: 0.3 * g,
    filter: 'bandpass',
    freq: 1300,
    freqEnd: 2600,
    q: 0.9,
  });
  tone(v, { type: 'sine', freq: 150, freqEnd: 250, at: t, duration: 0.12, peak: 0.25 * g });
}

/**
 * The double jump: the same scuff as `jump`, shorter and a fifth higher.
 *
 * Deliberately built from the take-off's own recipe rather than from a new
 * one. It is the second half of a thing the player already knows the sound
 * of, and what it has to say is "again, and it worked" - a sound with no
 * relation to the first would say "something else happened". The rise at the
 * end is what makes it read as an answer rather than as a repeat.
 */
export function doubleJump(v: Voicing, t: number, g: number): void {
  noiseBurst(v, {
    at: t,
    duration: 0.07,
    peak: 0.26 * g,
    filter: 'bandpass',
    freq: 1900,
    freqEnd: 3600,
    q: 1.1,
  });
  tone(v, { type: 'sine', freq: 225, freqEnd: 400, at: t, duration: 0.1, peak: 0.22 * g });
}

export function land(v: Voicing, t: number, g: number): void {
  noiseBurst(v, {
    at: t,
    duration: 0.2,
    peak: 0.55 * g,
    attack: 0.004,
    filter: 'lowpass',
    freq: 900,
    freqEnd: 150,
    q: 0.6,
  });
  tone(v, { type: 'sine', freq: 110, freqEnd: 48, at: t, duration: 0.22, peak: 0.6 * g });
}

export function doorOpen(v: Voicing, t: number, g: number): void {
  // Latch first, then the hinge complaining all the way through the swing.
  noiseBurst(v, { at: t, duration: 0.045, peak: 0.5 * g, filter: 'bandpass', freq: 2600, q: 3 });
  tone(v, {
    type: 'sawtooth',
    freq: 210,
    freqEnd: 380,
    at: t + 0.03,
    duration: 0.4,
    peak: 0.1 * g,
    attack: 0.07,
    vibrato: { depth: 24, hz: 13 },
    filter: { type: 'bandpass', freq: 900, q: 4 },
  });
  noiseBurst(v, {
    at: t + 0.03,
    duration: 0.4,
    peak: 0.16 * g,
    attack: 0.08,
    filter: 'bandpass',
    freq: 800,
    freqEnd: 1500,
    q: 6,
  });
}

/**
 * The other half of `doorOpen`, in the other order.
 *
 * The hinge runs first and shorter - a leaf being pushed shut is given one
 * shove rather than pulled the whole way - and then the two sounds an opening
 * never makes: the stile meeting the karmi, low and wooden, and the latch
 * dropping into the striker a hair after it. That last click is the whole cue.
 * Without it a door sounds like it bounced off the frame.
 */
export function doorClose(v: Voicing, t: number, g: number): void {
  tone(v, {
    type: 'sawtooth',
    freq: 360,
    freqEnd: 220,
    at: t,
    duration: 0.24,
    peak: 0.09 * g,
    attack: 0.05,
    vibrato: { depth: 22, hz: 13 },
    filter: { type: 'bandpass', freq: 900, q: 4 },
  });
  noiseBurst(v, {
    at: t,
    duration: 0.24,
    peak: 0.14 * g,
    attack: 0.06,
    filter: 'bandpass',
    freq: 1400,
    freqEnd: 700,
    q: 6,
  });
  // The leaf arriving: a wooden knock, which is the loudest thing here.
  noiseBurst(v, {
    at: t + 0.24,
    duration: 0.13,
    peak: 0.55 * g,
    attack: 0.002,
    filter: 'lowpass',
    freq: 900,
    freqEnd: 160,
    q: 0.8,
  });
  tone(v, { type: 'sine', freq: 165, freqEnd: 84, at: t + 0.24, duration: 0.12, peak: 0.4 * g });
  // And the latch a hair later, up where the handle is.
  noiseBurst(v, {
    at: t + 0.285,
    duration: 0.035,
    peak: 0.45 * g,
    filter: 'bandpass',
    freq: 2900,
    q: 3,
  });
}

export function bump(v: Voicing, t: number, g: number): void {
  noiseBurst(v, {
    at: t,
    duration: 0.18,
    peak: 0.5 * g,
    attack: 0.003,
    filter: 'lowpass',
    freq: 700,
    freqEnd: 120,
    q: 0.7,
  });
  tone(v, { type: 'sine', freq: 130, freqEnd: 62, at: t, duration: 0.16, peak: 0.5 * g });
}

export function stumble(v: Voicing, t: number, g: number): void {
  // Two scuffs: the foot that gives way, and the one that catches you.
  noiseBurst(v, {
    at: t,
    duration: 0.16,
    peak: 0.35 * g,
    attack: 0.02,
    filter: 'bandpass',
    freq: 900,
    freqEnd: 380,
    q: 1.2,
  });
  noiseBurst(v, {
    at: t + 0.1,
    duration: 0.2,
    peak: 0.28 * g,
    attack: 0.02,
    filter: 'bandpass',
    freq: 700,
    freqEnd: 300,
    q: 1.2,
  });
}

export function checkpoint(v: Voicing, t: number, g: number): void {
  tone(v, { type: 'triangle', freq: 880, at: t, duration: 0.09, peak: 0.4 * g, attack: 0.004 });
  tone(v, {
    type: 'triangle',
    freq: 1318.5,
    at: t + 0.075,
    duration: 0.15,
    peak: 0.35 * g,
    attack: 0.004,
  });
}

export function finish(v: Voicing, t: number, g: number): void {
  const arpeggio = [523.25, 659.25, 783.99, 1046.5];
  arpeggio.forEach((freq, i) => {
    const last = i === arpeggio.length - 1;
    tone(v, {
      type: 'triangle',
      freq,
      at: t + i * 0.075,
      duration: last ? 0.75 : 0.22,
      peak: (last ? 0.42 : 0.32) * g,
      attack: 0.005,
    });
  });
  tone(v, { type: 'sine', freq: 130.81, at: t, duration: 0.95, peak: 0.3 * g, attack: 0.02 });
}

/**
 * The yard losing its power: the clack of a plug leaving a socket, then
 * everything that was humming winding down together.
 *
 * The wind-down is the whole sound. A mains hum does not stop, it *falls* -
 * fans spinning down, a transformer letting go of its 50 Hz - so the tone
 * glides down an octave and a half while the noise under it closes, and the
 * silence at the end is the part the ear actually reads as "off".
 */
export function powerOff(v: Voicing, t: number, g: number): void {
  // The plug.
  noiseBurst(v, {
    at: t,
    duration: 0.09,
    peak: 0.55 * g,
    attack: 0.002,
    filter: 'bandpass',
    freq: 1900,
    freqEnd: 700,
    q: 1.4,
  });
  // Mains hum, dying. Sawtooth through a closing lowpass: the harmonics go
  // before the fundamental does, which is what a wind-down sounds like.
  tone(v, {
    type: 'sawtooth',
    freq: 100,
    freqEnd: 34,
    at: t + 0.02,
    duration: 1.15,
    peak: 0.4 * g,
    attack: 0.01,
    filter: { type: 'lowpass', freq: 1200, q: 0.7 },
  });
  // The room's fans, going with it.
  noiseBurst(v, {
    at: t + 0.03,
    duration: 1.0,
    peak: 0.3 * g,
    attack: 0.02,
    filter: 'lowpass',
    freq: 1400,
    freqEnd: 180,
    q: 0.8,
  });
}

/**
 * A window going in.
 *
 * Three things in this order, because that is the order glass does them: the
 * sheet cracking, the sheet letting go, and then a second and a bit of pieces
 * landing on grass. The crack is a bandpass burst high enough to sting; the
 * break under it is the same noise with the filter thrown open, which is what
 * turns a click into a shatter; and the fall is a long, quiet, closing burst
 * with no pitch in it at all.
 *
 * On top of that, six struck tones spread over the first third of a second.
 * That is the part the ear identifies as *glass* rather than as any other
 * smash: broken glass rings, every piece at its own frequency, and the pitches
 * are picked out of `v.rng` so no two windows sound bit-identical - out of the
 * seeded one, so one replay always breaks the same window the same way.
 *
 * Every cue in here is a fallback: `sfx/glass-break.m4a` is the sound, and this
 * is what plays if that file failed to load. It is not trying to beat the
 * recording, only to be unmistakably the same event.
 */
export function glassBreak(v: Voicing, t: number, g: number): void {
  // The crack. Short, bright, and the only part of this with a hard attack.
  noiseBurst(v, {
    at: t,
    duration: 0.055,
    peak: 0.7 * g,
    attack: 0.001,
    filter: 'bandpass',
    freq: 5200,
    freqEnd: 3000,
    q: 1.8,
  });
  // The sheet going. Wide open, so it reads as a sheet rather than as a tap.
  noiseBurst(v, {
    at: t + 0.01,
    duration: 0.3,
    peak: 0.55 * g,
    attack: 0.004,
    filter: 'highpass',
    freq: 1400,
    freqEnd: 2600,
    q: 0.7,
  });
  // The pieces ringing on the way out. Sine rather than anything richer: a
  // shard is close to a pure tone, and six sawtooths would be a car alarm.
  for (let i = 0; i < 6; i++) {
    const freq = 2400 + v.rng() * 3600;
    tone(v, {
      type: 'sine',
      freq,
      freqEnd: freq * 0.94,
      at: t + 0.005 + v.rng() * 0.3,
      duration: 0.12 + v.rng() * 0.22,
      peak: (0.06 + v.rng() * 0.07) * g,
      attack: 0.002,
    });
  }
  // And the takapiha's grass catching all of it.
  noiseBurst(v, {
    at: t + 0.16,
    duration: 0.85,
    peak: 0.2 * g,
    attack: 0.05,
    filter: 'bandpass',
    freq: 3400,
    freqEnd: 1500,
    q: 0.8,
  });
}

/**
 * The sauna's handle, and the reason there is a second door cue at all.
 *
 * `doorOpen` is a latch letting go and then half a second of hinge. This is
 * the same latch finding a striker that does not move: two clacks, because
 * nobody tries a locked door once, and then the leaf thumping its own frame as
 * the shove behind the second one arrives at a stop. There is no hinge in it,
 * and the absence is the whole message - a player who has heard `door-open`
 * three times already this run knows what is missing without being told.
 */
export function oviLukossa(v: Voicing, t: number, g: number): void {
  for (const [delay, level] of [
    [0, 1],
    [0.13, 0.82],
  ] as [number, number][]) {
    noiseBurst(v, {
      at: t + delay,
      duration: 0.035,
      peak: 0.55 * level * g,
      attack: 0.002,
      filter: 'bandpass',
      freq: 2400,
      freqEnd: 1700,
      q: 4,
    });
  }
  // The leaf itself, arriving at a frame that is not going anywhere. Low and
  // short: a shut door is a stiff thing and it does not ring.
  noiseBurst(v, {
    at: t + 0.14,
    duration: 0.12,
    peak: 0.34 * g,
    attack: 0.003,
    filter: 'lowpass',
    freq: 520,
    freqEnd: 130,
    q: 0.8,
  });
  tone(v, { type: 'sine', freq: 108, freqEnd: 58, at: t + 0.14, duration: 0.13, peak: 0.32 * g });
}

/**
 * A klapi coming off the top of the kasa: two bits of dry wood knocking
 * together and a scrape of bark, and no more than that. It is a sound you hear
 * at a run, so what it has to be is *short*.
 */
export function klapiNosto(v: Voicing, t: number, g: number): void {
  for (const [delay, freq] of [
    [0, 780],
    [0.045, 610],
  ] as const) {
    tone(v, {
      type: 'triangle',
      freq,
      freqEnd: freq * 0.55,
      at: t + delay,
      duration: 0.07,
      peak: 0.5 * g,
      attack: 0.002,
    });
  }
  noiseBurst(v, {
    at: t,
    duration: 0.13,
    peak: 0.3 * g,
    attack: 0.004,
    filter: 'bandpass',
    freq: 2600,
    freqEnd: 900,
    q: 0.9,
  });
}

/**
 * An empty tuoppi coming off the pile at the end of the baari: the ring of a
 * glass base parting from the one it was stacked in, and the little slosh of
 * what the last mouthful left in it.
 *
 * Higher and thinner than `klapi-nosto` on purpose. The two are the same
 * *event* - a press of F that put something in Kimmo's hand - and the whole
 * job of this one is to be unmistakably not that: wood is a knock, glass is a
 * ring, and a player who has heard both once should never have to wonder which
 * of the two he is carrying.
 */
export function tuoppiPoydalta(v: Voicing, t: number, g: number): void {
  // The base leaving the table. Two partials a fifth apart, which is what a
  // struck glass is and what a struck billet is not.
  for (const [delay, freq] of [
    [0, 1840],
    [0.012, 2760],
  ] as const) {
    tone(v, {
      type: 'sine',
      freq,
      freqEnd: freq * 0.985,
      at: t + delay,
      duration: 0.22,
      peak: 0.3 * g,
      attack: 0.001,
    });
  }
  // And the dregs moving in the bottom of it.
  noiseBurst(v, {
    at: t + 0.02,
    duration: 0.11,
    peak: 0.16 * g,
    attack: 0.006,
    filter: 'bandpass',
    freq: 1100,
    freqEnd: 520,
    q: 1.4,
  });
}

/**
 * And the same glass filling under a hana: the handle going over, a second of
 * beer hitting glass, and the head settling once the tap is shut.
 *
 * The longest one-shot in the game, and the only one that is a process rather
 * than an impact. The pitch of the stream *falls* as it goes - a glass filling
 * up is a resonant column getting shorter, which every ear knows without ever
 * having been told - and that fall is what says the glass is full at the end
 * of it rather than merely wet.
 */
export function hanaAuki(v: Voicing, t: number, g: number): void {
  // The handle. A short wooden knock, because that is what the kahva is.
  tone(v, { type: 'square', freq: 280, freqEnd: 170, at: t, duration: 0.07, peak: 0.3 * g, attack: 0.002 });
  // The pour. Bandpassed noise sweeping down as the column shortens, and it
  // runs the length of `SIMA.pourDuration` on purpose: this cue is a clock
  // now. The handle goes down on the press, the noise covers the two seconds
  // the glass is actually filling for, and the knock at the end of it lands on
  // the step the corridor opens. A shorter one would say the round was bought
  // while the player was still standing there waiting for it.
  noiseBurst(v, {
    at: t + 0.05,
    duration: 1.9,
    peak: 0.4 * g,
    attack: 0.05,
    filter: 'bandpass',
    freq: 1500,
    freqEnd: 460,
    q: 1.1,
  });
  // The tap shutting, and the head settling on top of what is now in there.
  tone(v, { type: 'square', freq: 250, freqEnd: 320, at: t + 1.95, duration: 0.06, peak: 0.24 * g, attack: 0.002 });
  noiseBurst(v, {
    at: t + 1.99,
    duration: 0.5,
    peak: 0.14 * g,
    attack: 0.03,
    filter: 'highpass',
    freq: 4200,
    freqEnd: 6400,
    q: 0.6,
  });
}

/**
 * And the same klapi going in: the clang of the plate, the log landing on a
 * bed of embers, and the draught the open hatch pulls through the firebox.
 */
export function klapiPesaan(v: Voicing, t: number, g: number): void {
  // The hatch.
  tone(v, { type: 'square', freq: 340, freqEnd: 190, at: t, duration: 0.1, peak: 0.35 * g, attack: 0.002 });
  // The log going down on the embers.
  noiseBurst(v, {
    at: t + 0.06,
    duration: 0.2,
    peak: 0.45 * g,
    attack: 0.004,
    filter: 'lowpass',
    freq: 900,
    freqEnd: 160,
    q: 0.7,
  });
  // The fire taking, which is the part that says the veräjä is opening.
  noiseBurst(v, {
    at: t + 0.13,
    duration: 0.75,
    peak: 0.34 * g,
    attack: 0.12,
    filter: 'bandpass',
    freq: 420,
    freqEnd: 1500,
    q: 0.6,
  });
}

/**
 * A makkara landing on the plate: the fork hitting china, and the meat after
 * it.
 *
 * Short and bright, and pitched *up* rather than down. It is the one sound in
 * the minigame that means "keep going", and a second of a run is spent
 * deciding whether it was that one or the other - so the two are separated by
 * the thing an ear reads fastest, which is which way the pitch went.
 */
export function makkaraLautaselle(v: Voicing, t: number, g: number): void {
  // The china.
  tone(v, {
    type: 'triangle',
    freq: 1180,
    freqEnd: 1560,
    at: t,
    duration: 0.09,
    peak: 0.42 * g,
    attack: 0.002,
  });
  // And the weight of it going down on the plate.
  noiseBurst(v, {
    at: t + 0.02,
    duration: 0.11,
    peak: 0.3 * g,
    attack: 0.004,
    filter: 'lowpass',
    freq: 1400,
    freqEnd: 380,
    q: 0.8,
  });
}
